/**
 * ReportService
 *
 * Purpose
 * - Strongly-typed wrapper around the RPMS reporting/export endpoints.
 * - Centralizes baseUrl resolution (from AppConfig.settings) and shared HTTP headers.
 * - Keeps call sites simple: each method returns an HttpClient Observable for the specific API.
 *
 * Usage
 * - App initialization:
 *   - Ensure AppConfig is registered and loaded via APP_INITIALIZER so AppConfig.settings is available at startup.
 *     In AppModule:
 *       export function initializeApp(cfg: AppConfig) { return () => cfg.load(); }
 *       { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AppConfig], multi: true }
 * - Injection:
 *   - constructor(private reportService: ReportService) {}
 * - Calls:
 *   - reportService.getBillingProfitReport(storeId, reportType).subscribe(...);
 * - Auth:
 *   - AuthInterceptor attaches Authorization: Bearer <token> automatically to all API calls (except /Token).
 *
 * Changes since previous implementation
 * - Fixed early-read of AppConfig.settings:
 *   - Moved baseUrl initialization from a field initializer to the constructor so it runs after APP_INITIALIZER.
 *   - Injected AppConfig to make the dependency explicit and to allow DI to order construction correctly.
 *   - Added a defensive warning if baseUrl is unexpectedly missing (helps diagnose bootstrap timing issues).
 * - Added ensureBaseUrl() to re-check AppConfig.settings.baseUrl on first use if construction happened before config loaded.
 * - Safety on logging: logClientMessage returns an Observable and no-ops via of(null) if token/baseUrl are missing (prevents 401 loops during logout).
 * - No endpoint shapes or routes were changed; all existing URLs and return types are preserved.
 *
 * Operational notes
 * - All methods return Observables; callers should subscribe and handle success/error as needed.
 * - If you add new endpoints, prefer path parameters that are already URL-safe; otherwise encode user-provided values.
 * - This service is used by GlobalErrorHandler; AppConfig must be a dependency of the error handler provider to guarantee ordering.
 */

import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { AppConfig } from '../app.config';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  // Do NOT read AppConfig.settings at declaration time; it can be undefined before APP_INITIALIZER completes.
  private _baseUrl: string;
  // Legacy fallback example (kept for reference): // private _baseUrl = environment.baseUrl;

  // Shared headers for report endpoints (adjust per endpoint as needed).
  private _reqHeader = new HttpHeaders({ 'Accept': 'application/json', 'Content-Type': 'application/json' });

  constructor(
    private _http: HttpClient,
    // Inject AppConfig so DI guarantees construction after APP_INITIALIZER has populated settings.
    private _appConfig: AppConfig
  ) {
    // Safe to read after APP_INITIALIZER resolved
    this._baseUrl = AppConfig.settings?.baseUrl;
    if (!this._baseUrl) {
      // Defensive log to help diagnose misordered bootstrap should it ever occur.
      // Avoid throwing here; individual calls will fail clearly if baseUrl remains falsy.
      console.warn('ReportService: AppConfig.settings.baseUrl is not initialized.');
      this._baseUrl = '';
    }
  }

  // Re-check baseUrl on demand (first real use) in case construction preceded APP_INITIALIZER completion.
  private ensureBaseUrl(): string {
    if (!this._baseUrl && AppConfig.settings?.baseUrl) {
      this._baseUrl = AppConfig.settings.baseUrl;
    }
    return this._baseUrl || '';
  }

  /**
   * Warehouse cost changes for a store within a date range.
   */
  getWarehouseCostChangesPerStoreAndDateRange(storeId, dateStart, dateEnd) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/WarehouseCostChangesPerStoreAndDateRange/' + storeId + '/' + dateStart + '/' + dateEnd,
      { headers: this._reqHeader }
    );
  }

  /**
   * POS export by store, week-ending date (or 'none'), and filter.
   */
  posExportByStoreIdDateAndFilter(storeId, weekEndingDateOrNone, filter) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/PosExportByStoreIdDateAndFilter/' + storeId + '/' + weekEndingDateOrNone + '/' + filter,
      { headers: this._reqHeader }
    );
  }

  /**
   * Week-ending dates used for POS export, constrained by filter.
   */
  getWeekEndingDatesForPosExport(filter) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/GetWeekEndingDatesForPosExport/' + filter,
      { headers: this._reqHeader }
    );
  }

  /**
   * Delimited POS export; returns a blob with response headers (e.g., filename).
   */
  delimitedPosExportByStoreIdDateAndFilter(storeId, weekEndingDateOrNone, filter) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/DelimitedPosExportByStoreIdDateAndFilter/' + storeId + '/' + weekEndingDateOrNone + '/' + filter,
      { headers: this._reqHeader, responseType: 'blob', observe: 'response' }
    );
  }

  /**
   * Billing profit report for a store by report type.
   */
  getBillingProfitReport(storeId, reportType) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/BillingProfitReportByStoreAndReportType/' + storeId + '/' + reportType,
      { headers: this._reqHeader }
    );
  }
  
  /**
   * Price change statistics (committed) for various scopes and date range.
   */
  getPriceChangeStats(storeGroupIdOr0, storeIdOr0, usernameOrNone, dateStartOrNone, dateEndOrNone) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/PriceChangeStats/' + storeGroupIdOr0 + '/' + storeIdOr0 + '/' + usernameOrNone + '/' + dateStartOrNone + '/' + dateEndOrNone,
      { headers: this._reqHeader }
    );
  }

  /**
   * Price change statistics (preview) for various scopes and date range.
   */
  getPreviewPriceChangeStats(storeGroupIdOr0, storeIdOr0, usernameOrNone, dateStartOrNone, dateEndOrNone) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/PreviewPriceChangeStats/' + storeGroupIdOr0 + '/' + storeIdOr0 + '/' + usernameOrNone + '/' + dateStartOrNone + '/' + dateEndOrNone,
      { headers: this._reqHeader }
    );
  }

  /**
   * Archival preview price changes for a range, optionally excluding mainframe items.
   */
  getArchivalPreviewPriceChanges(storeGroupIdOr0, storeIdOr0, usernameOrNone, dateStartOrNone, dateEndOrNone, excludeMainframe) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/DenormalizedPreviewPriceChanges/' + storeGroupIdOr0 + '/' + storeIdOr0 + '/' + usernameOrNone + '/' + dateStartOrNone + '/' + dateEndOrNone + '/' + excludeMainframe,
      { headers: this._reqHeader }
    );
  }

  /**
   * Reporting audit entries for a scope, date range, and report name.
   */
  getReportingAudit(storeGroupIdOr0, storeIdOr0, usernameOrNone, dateStartOrNone, dateEndOrNone, reportNameOrNone) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/ReportsAndExports/ReportingAudit/' + storeGroupIdOr0 + '/' + storeIdOr0 + '/' + usernameOrNone + '/' + dateStartOrNone + '/' + dateEndOrNone + '/' + reportNameOrNone,
      { headers: this._reqHeader }
    );
  }

  /**
   * Log actions for a date range.
   */
  getLogActionsByDateRange(dateStart, dateEnd) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/LogActions/GetLogActionsByDateRange/' + dateStart + '/' + dateEnd,
      { headers: this._reqHeader }
    );
  }

  /**
   * Log actions by user within a date range.
   */
  getLogActionsByUserIdAndDateRange(userId, dateStart, dateEnd) {
    const base = this.ensureBaseUrl();
    return this._http.get(
      base + '/api/LogActions/GetLogActionsByUserIdAndDateRange/' + userId + '/' + dateStart + '/' + dateEnd,
      { headers: this._reqHeader }
    );
  }

  /**
   * Sends a client-side log message to the server.
   * Safety:
   * - If baseUrl or token is missing (e.g., during/after logout), skip the call to avoid 401 recursion loops.
   * - Returns an Observable (of(null)) so callers can safely subscribe without null checks.
   */
  logClientMessage(msg) {
    const base = this.ensureBaseUrl();
    const hasToken = !!sessionStorage.getItem('rpmsToken');
    if (!base || !hasToken) {
      return of(null);
    }
    return this._http.post(
      base + '/api/LogActions/LogClientMessage',
      msg,
      { headers: this._reqHeader }
    );
  }
}
